import { useState, useEffect } from "react";

import { filterOnlyUniqueByKey, removeNullable } from "services/apiHelpers";
import { DEFAULT_EMPTY_VALUE } from "util/statHelpers";

const filterOnlyUniqueValue = filterOnlyUniqueByKey("value");

type ValueEntryValue = string | number | SelectOption;
type ValueEntry = Record<string, ValueEntryValue>;
export type ValueEntries = ValueEntry[];

const valueEntryToSelectOption = (value: ValueEntryValue): SelectOption =>
  typeof value === "object"
    ? value
    : {
        value: value as string,
        label: value as string,
      };

const createOptions = (values: ValueEntries, valueKey: string) =>
  removeNullable(
    values.map((value) =>
      value[valueKey] && value[valueKey] !== DEFAULT_EMPTY_VALUE
        ? valueEntryToSelectOption(value[valueKey])
        : null
    )
  ).filter(filterOnlyUniqueValue);

const useSelects = ({
  values,
  selectMetadata,
}: {
  values?: ValueEntries;
  selectMetadata: Pick<SelectState, "placeholder" | "filterKey">[];
}): {
  selectStates: SelectState[];
} => {
  const [selectStates, setSelectStates] = useState<SelectState[]>(
    selectMetadata.map((option) => ({
      ...option,
      options: values ? createOptions(values, option.filterKey) : [],
    }))
  );

  useEffect(() => {
    if (values) {
      if (values.length > 0) {
        setSelectStates((existingStates) => {
          // Options are additive, so the existing selectStates must be retained.
          const combineOptions = (key: string) =>
            [
              ...(existingStates.find((option) => option.filterKey === key)
                ?.options ?? []),
              ...createOptions(values, key),
            ].filter(filterOnlyUniqueValue);
          return existingStates.map((existingState) => ({
            ...existingState,
            options: combineOptions(existingState.filterKey),
          }));
        });
      }
    } else {
      setSelectStates(
        selectMetadata.map((option) => ({
          ...option,
          options: [],
        }))
      );
    }
  }, [selectMetadata, values]);

  return {
    selectStates,
  };
};

export default useSelects;
